import { IAddress, IOfficeInfo, IParkingInformation } from '~/models';
import { IUser } from '~/models/User';

/**
 * Top-level IUser fields that move to GET /office/:userId (GOTU-4961).
 * SINGLE SOURCE OF TRUTH (in this repo): drives the type union below, the compile guard
 * (`types.assert.ts`), AND the runtime merge (`mergeOfficeDetail`) — so they can't drift.
 *
 * Authoritative cross-repo contract: tempmee_server/src/utils/office/types.ts
 * (`OfficeContractResponse`). Each client mirrors this moved-field set — keep in sync when
 * adding/removing a moved field.
 */
export const MOVED_OFFICE_TOP_LEVEL = [
  'office_name',
  'web_site',
  'profile_image',
  'average_rating',
  'dsoName',
  'address',
  'parkingInformation',
  'childOffices',
] as const;
export type TMovedOfficeTopLevel = (typeof MOVED_OFFICE_TOP_LEVEL)[number];

/** office_info subfields that move to GET /office/:userId (same single-source pattern). */
export const MOVED_OFFICE_INFO = [
  'officeType',
  'insurances',
  'ramVerified',
  'timePerPatientRDH',
  'timePerPatientDA',
  'timePerPatientDN',
  'isAssistedHygiene',
  'productivity',
  'bestInDental',
  'onboardingStartDate',
] as const;
export type TMovedOfficeInfo = (typeof MOVED_OFFICE_INFO)[number];

/** Slim child-office shape from GET /office/:userId — NOT a full IUser. */
export interface IOfficeContractChild {
  readonly id: string;
  readonly office_name?: string;
  readonly profile_image?: string;
  readonly address?: IAddress[];
}

/**
 * WIRE shape of GET /office/:userId — mirrors `OfficeContractResponse`
 * (tempmee_server/src/utils/office/types.ts). `ramVerified` is a string enum;
 * `onboardingStartDate` arrives as an ISO string over HTTP.
 */
export interface IOfficeContractResponse {
  readonly id: string;
  readonly office_name: string;
  readonly web_site?: string;
  readonly profile_image?: string;
  readonly average_rating?: number;
  readonly dsoName?: string;
  readonly address: IAddress[];
  // Derived from MOVED_OFFICE_INFO (minus the two org-service-only fields, which aren't on
  // IOfficeInfo) so the wire shape and the compile guard share one source of truth — adding a
  // moved subfield updates both at once. `ramVerified`/`onboardingStartDate` are declared
  // separately because they don't exist on the monolith `IOfficeInfo`.
  readonly office_info: Pick<
    IOfficeInfo,
    Exclude<TMovedOfficeInfo, 'ramVerified' | 'onboardingStartDate'>
  > & {
    readonly ramVerified?: string;
    readonly onboardingStartDate?: string;
  };
  readonly parkingInformation?: IParkingInformation | null;
  readonly childOffices: IOfficeContractChild[];
}

/**
 * The office-read /auth/me?x-office response with the moved office fields REMOVED from the
 * TYPE, so reading a moved field off it is a compile error (the AC guardrail). Runtime may
 * still carry them; the type is the enforcement point.
 */
export type TAuthMeResponse = Omit<IUser, TMovedOfficeTopLevel | 'office_info'> & {
  readonly office_info: Omit<IOfficeInfo, TMovedOfficeInfo>;
};

/**
 * Merged selected-office object handed downstream. Stays `IUser`-shaped so no consumer
 * changes. `childOffices` keeps the `IUser[]` slot but under flag ON holds slim
 * `IOfficeContractChild` objects (a deliberate lossy cast in the merge — a consumer reading
 * a non-slim child field gets `undefined` under flag ON).
 */
export type TSelectedOffice = IUser;
